import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ui } from './ui/tokens';
import { GoldButton, OutlineButton } from './ui/Buttons';
import TimerIcon from '../assets/design/timer.svg';

const pad = (n: number) => String(n).padStart(2, '0');
const formatElapsed = (totalSeconds: number) => {
	const h = Math.floor(totalSeconds / 3600);
	const m = Math.floor((totalSeconds % 3600) / 60);
	const s = totalSeconds % 60;
	return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

/**
 * "Time spent on this creation" (style guide R18 / R14 step 7). Starts when
 * the editor opens and keeps counting while hidden; Stop pauses it.
 * Session only: the time is not stored with the artwork, which needs the API.
 */
function DisplayTimer() {
	const [visible, setVisible] = useState(false);
	const [running, setRunning] = useState(true);
	const [elapsed, setElapsed] = useState(0);
	// Time banked before the current run, and when the current run started.
	// Deriving elapsed from timestamps avoids interval drift and keeps counting
	// correctly if the WebView throttles timers in the background.
	const bankedRef = useRef(0);
	const startedAtRef = useRef<number | null>(Date.now());

	useEffect(() => {
		const tick = () => {
			const runningFor = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
			setElapsed(Math.floor((bankedRef.current + runningFor) / 1000));
		};
		tick();
		const id = setInterval(tick, 500);
		return () => clearInterval(id);
	}, []);

	const stop = () => {
		if (!startedAtRef.current) return;
		bankedRef.current += Date.now() - startedAtRef.current;
		startedAtRef.current = null;
		setRunning(false);
	};

	const start = () => {
		if (startedAtRef.current) return;
		startedAtRef.current = Date.now();
		setRunning(true);
	};

	if (!visible) {
		return (
			<ShowButton type="button" onClick={() => setVisible(true)}>
				<img src={TimerIcon} alt="" width={26} height={26} draggable={false} />
				<SmallLabel>Display timer</SmallLabel>
			</ShowButton>
		);
	}

	return (
		<Panel aria-label="Creation timer">
			<Time aria-live="off">{formatElapsed(elapsed)}</Time>
			<Meta>
				<SmallLabel>Time spent on this creation</SmallLabel>
				<SmallLabel aria-hidden>|</SmallLabel>
				<HideButton type="button" onClick={() => setVisible(false)}>
					<SmallLabel>Hide timer</SmallLabel>
				</HideButton>
			</Meta>
			<Actions>
				<OutlineButton type="button" onClick={stop} disabled={!running}>
					Stop
				</OutlineButton>
				<GoldButton type="button" onClick={start} disabled={running}>
					Start
				</GoldButton>
			</Actions>
		</Panel>
	);
}

export default DisplayTimer;

const ShowButton = styled.button`
	margin: 18px auto 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	background: none;
	border: none;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;
`;

const Panel = styled.section`
	margin-top: 15px;
	padding: 14px 16px 16px;
	background-color: ${ui.bar};
	border-radius: 12px;
	text-align: center;
	font-family: ${ui.font};
`;

const Time = styled.div`
	font-size: 40px;
	font-weight: 600;
	line-height: 1.1;
	color: ${ui.gold};
	font-variant-numeric: tabular-nums;
`;

const Meta = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	margin-top: 4px;
`;

const SmallLabel = styled.span`
	font-family: ${ui.font};
	font-size: 9px;
	font-weight: 700;
	text-transform: uppercase;
	color: ${ui.label};
`;

const HideButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	text-decoration: underline;
	text-decoration-color: ${ui.label};
`;

const Actions = styled.div`
	display: flex;
	gap: 12px;
	margin-top: 12px;

	button {
		height: 40px;
	}
`;
